import type { CSSProperties } from "react";
import { resolvedCtaUrl } from "@/app/externalNavigation";
import { getRuntimeStatusMessage } from "@/app/runtimeStatus";
import { watchHotspots, watchVariants } from "@/demos/explore/config";
import type { DemoAction, DemoControlState, DemoId } from "@/demos/types";
import type { RuntimeMode, RuntimePhase } from "@/runtime/types";

export type LandingPanelProps =
  | { variant: "landing"; onStart: () => void; onVisit: () => void }
  | { variant: "compatibility"; onVisit: () => void };

export function LandingPanel(props: LandingPanelProps) {
  if (props.variant === "compatibility") {
    return (
      <section className="landing-panel compatibility-panel" role="alert" data-testid="webgl-unavailable">
        <h1>3D GRAPHICS<br /><em>UNAVAILABLE.</em></h1>
        <p className="landing-note">This browser cannot create the graphics context required for this experience. Try a browser with hardware acceleration enabled.</p>
        <a className="start-button" href={resolvedCtaUrl()} target="_blank" rel="noopener noreferrer" onClick={props.onVisit}>
          <span>Visit Rising Pixel</span><i aria-hidden="true">→</i>
        </a>
      </section>
    );
  }

  return (
    <section className="landing-panel" data-testid="landing">
      <h1>SEE THE<br /><em>EXTRAORDINARY.</em></h1>
      <button className="start-button" type="button" onClick={props.onStart} data-ui-control data-testid="start-ar">
        <span>Start experience</span><i aria-hidden="true">→</i>
      </button>
      <p className="landing-note">Camera access is requested only after you continue.</p>
      <a className="landing-link" href={resolvedCtaUrl()} target="_blank" rel="noopener noreferrer" onClick={props.onVisit}>Visit Rising Pixel</a>
    </section>
  );
}

export interface StatusPanelProps {
  phase: RuntimePhase;
  mode: RuntimeMode;
  progress: number;
  error: string | null;
  notice: string | null;
  onRetry: () => void;
  onContinue: () => void;
}

export function StatusPanel({ phase, mode, progress, error, notice, onRetry, onContinue }: StatusPanelProps) {
  if (phase === "placed") {
    const message = notice ?? (mode === "camera-viewer" ? "Camera preview · tracking unavailable" : null);
    return message ? <div className="mode-notice" role="status" aria-live="polite">{message}</div> : null;
  }
  if (phase === "error") return (
    <div className="status-panel status-error" role="alert" data-ui-control>
      <p>{error ?? "The immersive experience could not start."}</p>
      <div><button type="button" onClick={onRetry}>Try again</button><button type="button" onClick={onContinue}>Continue in 3D</button></div>
    </div>
  );
  return (
    <div className="status-panel" role="status" aria-live="polite" data-testid="runtime-status">
      <div className="stability-ring" role="progressbar" aria-label="Placement progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} style={{ "--placement-progress": progress } as CSSProperties}><i /></div>
      <p>{getRuntimeStatusMessage(phase)}</p>
    </div>
  );
}

export function DemoControls({ id, controls, dispatch }: { id: DemoId; controls: DemoControlState; dispatch: (action: DemoAction) => void }) {
  if (id === "see") return (
    <div className="demo-controls compact-controls" data-ui-control>
      {(["none", "laptop", "phone"] as const).map((value) => <button key={value} className={controls.seeFocus === value ? "active" : ""} type="button" onClick={() => dispatch({ type: "see-focus", value })}>{value === "none" ? "Overview" : value}</button>)}
    </div>
  );
  if (id === "explore") {
    const hotspot = controls.exploreHotspot === "none" ? null : watchHotspots[controls.exploreHotspot];
    return (
      <div className="demo-controls configurator-panel" data-ui-control data-testid="explore-controls">
        <div className="control-row watch-variants">
          <span>Finish</span>
          {watchVariants.map((variant) => (
            <button
              key={variant.id}
              className={`swatch ${controls.exploreVariant === variant.id ? "active" : ""}`}
              style={{ background: variant.color }}
              aria-label={`${variant.label} finish`}
              aria-pressed={controls.exploreVariant === variant.id}
              title={variant.label}
              type="button"
              onClick={() => dispatch({ type: "explore-variant", value: variant.id })}
            />
          ))}
        </div>
        <div className="control-row segmented" aria-label="Watch inspection mode">
          <span>View</span>
          {(["assembled", "technical"] as const).map((value) => (
            <button key={value} type="button" className={controls.exploreInspection === value ? "active" : ""} aria-pressed={controls.exploreInspection === value} onClick={() => dispatch({ type: "explore-inspection", value })}>{value}</button>
          ))}
        </div>
        <div className="control-row segmented">
          <span>Motion</span>
          <button type="button" className={controls.exploreAnimation === "running" ? "active" : ""} aria-pressed={controls.exploreAnimation === "running"} onClick={() => dispatch({ type: "explore-animation", value: controls.exploreAnimation === "running" ? "paused" : "running" })}>{controls.exploreAnimation === "running" ? "Pause seconds" : "Run seconds"}</button>
          <button type="button" onClick={() => dispatch({ type: "explore-reset" })}>Reset</button>
        </div>
        {hotspot ? (
          <aside className="hotspot-panel" aria-live="polite" data-testid="explore-hotspot-panel">
            <div><strong>{hotspot.label}</strong><button type="button" aria-label="Close product details" onClick={() => dispatch({ type: "explore-hotspot", value: "none" })}>×</button></div>
            <p>{hotspot.description}</p>
          </aside>
        ) : null}
      </div>
    );
  }
  return (
    <div className="demo-controls compact-controls" data-ui-control data-testid="engage-controls">
      <button type="button" className={controls.engageContent === "reference" ? "active" : ""} onClick={() => dispatch({ type: "engage-content", value: controls.engageContent === "reference" ? "media" : "reference" })}>{controls.engageContent === "reference" ? "Media state" : "Reference state"}</button>
      <button type="button" aria-pressed={controls.engageSide === "back"} onClick={() => dispatch({ type: "engage-side", value: controls.engageSide === "front" ? "back" : "front" })}>Flip card</button>
      {controls.engageContent === "media" ? <button type="button" onClick={() => dispatch({ type: "engage-playing", value: !controls.engagePlaying })} disabled={controls.engageMediaError}>{controls.engagePlaying ? "Pause" : "Play"} media</button> : null}
      {controls.engageMediaError ? <span>Poster mode</span> : null}
    </div>
  );
}
